import {
	Box,
	Button,
	CardActions,
	CardContent,
	Divider,
	Grid,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppRichTextEditor from '@/common/components/editor/AppRichTextEditor';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import dayjs from 'dayjs';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import { useUpdateExplainMutation } from '@/api/treasury/explainApiSlice';
import { GameDescriptionDTO } from '@/api/DTO/treasury/explain.interface';

interface Props {
	type: FormType;
	setType: Dispatch<SetStateAction<FormType>>;
	detail?: GameDescriptionDTO;
}

export default function ExplainForm({ type, setType, detail }: Props) {
	const { t } = useTranslation();
	const [updateExplain] = useUpdateExplainMutation();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const defaultValues: DefaultValues<GameDescriptionDTO> = detail || {};

	const formSchema = Yup.object().shape({
		descriptionZh: Yup.string().required(t('errorMessage.required')),
	});
	const methods = useForm<GameDescriptionDTO>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<GameDescriptionDTO> = async (data) => {
		const response = await updateExplain(data);
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.changeContentSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			setType(FormType.view);
		}
	};

	const handleClose = () => {
		setType(FormType.view);
	};
	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Box display='flex' justifyContent='space-between'>
							<Typography variant='h6'>{t('meditation.record')}</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView
									label={t('ruleList.updateTime')}
									value={
										!detail?.updatedAt
											? '-'
											: dayjs(detail?.updatedAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
						</Grid>
						<Divider />
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ padding: '24px 0' }}
						>
							<Typography variant='h6'>{t('meditation.text')}</Typography>
						</Box>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{currentTab == 'zh' && (
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('ruleList.description')}
									</Typography>
									<AppRichTextEditor
										editorTheme='style2'
										control={methods.control}
										name='descriptionZh'
									/>
								</Grid>
							)}
							{currentTab == 'en' && (
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('ruleList.description')}
									</Typography>
									<AppRichTextEditor
										editorTheme='style2'
										control={methods.control}
										name='descriptionEn'
									/>
								</Grid>
							)}
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.create')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
